/**
 * ErrorBoundary amélioré avec classification d'erreurs,
 * retry automatique, et intégration mode dégradé
 */

import { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home, MessageCircle, WifiOff, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { captureError, addBreadcrumb } from '@/lib/sentry';
import { classifyError, ClassifiedError, ErrorCategory, ErrorSeverity } from '@/lib/errorClassification';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** ID de la fonctionnalité pour le tracking */
  featureId?: string;
  /** Callback quand une erreur est capturée */
  onError?: (error: ClassifiedError) => void;
  /** Nombre maximum de retry automatiques */
  maxRetries?: number;
  /** Afficher les options de récupération */
  showRecoveryOptions?: boolean;
}

interface State {
  hasError: boolean;
  error?: ClassifiedError;
  retryCount: number;
  isRetrying: boolean;
}

export class ErrorBoundaryEnhanced extends Component<Props, State> {
  private retryTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      retryCount: 0,
      isRetrying: false 
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { 
      hasError: true, 
      error: classifyError(error)
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const classified = classifyError(error);
    
    // Envoyer à Sentry avec contexte enrichi
    captureError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      featureId: this.props.featureId,
      category: classified.category,
      severity: classified.severity,
      isRetryable: classified.isRetryable,
      retryCount: this.state.retryCount
    });
    
    addBreadcrumb('ErrorBoundaryEnhanced caught error', {
      errorMessage: error.message,
      category: classified.category,
      severity: classified.severity,
      featureId: this.props.featureId
    });

    // Callback externe
    this.props.onError?.(classified);

    // Retry automatique si applicable
    const maxRetries = this.props.maxRetries ?? 2;
    if (classified.isRetryable && this.state.retryCount < maxRetries && classified.retryAfterMs) {
      this.scheduleRetry(classified.retryAfterMs);
    }

    console.error('[ErrorBoundary]', {
      message: error.message,
      category: classified.category,
      severity: classified.severity,
      retryCount: this.state.retryCount
    });
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  scheduleRetry(delayMs: number) {
    this.setState({ isRetrying: true });
    this.retryTimeoutId = setTimeout(() => {
      this.setState(s => ({ 
        hasError: false, 
        error: undefined, 
        retryCount: s.retryCount + 1,
        isRetrying: false
      }));
    }, delayMs);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, isRetrying: false });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleContact = () => {
    window.location.href = '/contact';
  };

  getIcon() {
    const category = this.state.error?.category;
    switch (category) {
      case ErrorCategory.NETWORK:
        return <WifiOff className="h-12 w-12 text-warning mx-auto mb-4" />;
      case ErrorCategory.AUTH:
        return <Clock className="h-12 w-12 text-destructive mx-auto mb-4" />;
      default:
        return <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />;
    }
  }

  getSeverityColor(): string {
    switch (this.state.error?.severity) {
      case ErrorSeverity.LOW:
        return 'border-yellow-500/50';
      case ErrorSeverity.MEDIUM:
        return 'border-orange-500/50';
      case ErrorSeverity.HIGH:
      case ErrorSeverity.CRITICAL:
        return 'border-destructive/50';
      default:
        return 'border-border';
    }
  }

  render() {
    const { hasError, error, isRetrying } = this.state;
    const { fallback, showRecoveryOptions = true } = this.props;

    if (!hasError) {
      return this.props.children;
    }

    // Fallback custom
    if (fallback) {
      return fallback;
    }

    // État de retry en cours
    if (isRetrying) {
      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <RefreshCw className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
              <p className="text-muted-foreground">
                Nouvelle tentative en cours...
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-[400px] flex items-center justify-center p-4">
        <Card className={`max-w-md w-full border-2 ${this.getSeverityColor()}`}>
          <CardHeader className="text-center">
            {this.getIcon()}
            <CardTitle className="text-destructive">
              {error?.category === ErrorCategory.NETWORK 
                ? 'Problème de connexion' 
                : 'Une erreur s\'est produite'}
            </CardTitle>
            <CardDescription>
              {error?.userMessage || 'Nous nous excusons pour ce problème.'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Message technique en dev */}
            {import.meta.env.DEV && error && (
              <div className="bg-muted p-3 rounded-lg text-xs font-mono text-destructive break-all">
                <div className="font-bold mb-1">
                  [{error.category}] {error.severity}
                </div>
                {error.technicalMessage}
              </div>
            )}
            
            {/* Actions de récupération */}
            {showRecoveryOptions && (
              <div className="flex flex-wrap justify-center gap-2">
                {error?.isRetryable && (
                  <Button 
                    variant="default"
                    onClick={this.handleRetry}
                    className="gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Réessayer
                  </Button>
                )}
                
                <Button 
                  variant="outline" 
                  onClick={this.handleRefresh}
                  className="gap-2"
                >
                  Actualiser la page
                </Button>

                {error?.suggestedAction === 'contact_support' && (
                  <Button 
                    variant="secondary"
                    onClick={this.handleContact}
                    className="gap-2"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Contacter le support
                  </Button>
                )}

                <Button 
                  variant="ghost"
                  onClick={this.handleGoHome}
                  className="gap-2"
                >
                  <Home className="h-4 w-4" />
                  Retour à l'accueil
                </Button>
              </div>
            )}

            {/* Indication de retry automatique */}
            {error?.isRetryable && (this.props.maxRetries ?? 2) > this.state.retryCount && (
              <p className="text-xs text-center text-muted-foreground">
                Tentatives restantes: {(this.props.maxRetries ?? 2) - this.state.retryCount}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
}

// Export de l'ancien nom pour compatibilité
export { ErrorBoundaryEnhanced as ErrorBoundary };
